import React, { useEffect, useRef, useState } from 'react'
import { Button, Result, Spin } from 'antd'
import { useHistory, useLocation } from 'react-router-dom'
import { useAppDispatch } from '~/Store/hooks'
import { UserService } from '~/Services'
import { UserActions } from '~/Store/User'
import { Utils } from '~/Utils'
import {
  clearPkceTransaction,
  decodeIdTokenNonce,
  getStoredCodeVerifier,
  getStoredNonce,
  getStoredReturnTo,
  getStoredState,
} from '~/Utils/pkce'

type CallbackStatus = 'processing' | 'error'

/** Callback từ `/connect/authorize`: validate state → đổi code lấy token → lưu token → quay lại trang ban đầu. */
export const Callback = () => {
  const dispatch = useAppDispatch()
  const history = useHistory()
  const location = useLocation()
  const [status, setStatus] = useState<CallbackStatus>('processing')
  const [errorMessage, setErrorMessage] = useState('')
  const ranOnce = useRef(false)

  useEffect(() => {
    // React 17 + StrictMode có thể chạy effect 2 lần ở dev — code chỉ dùng được 1 lần (OpenIddict
    // reject reuse), guard để tránh gọi exchangeCodeForToken 2 lần cho cùng 1 code.
    if (ranOnce.current) return
    ranOnce.current = true

    const fail = (message: string) => {
      clearPkceTransaction()
      setErrorMessage(message)
      setStatus('error')
    }

    const run = async () => {
      const params = new URLSearchParams(location.search)
      const oauthError = params.get('error')
      const code = params.get('code')
      const returnedState = params.get('state')

      if (oauthError) {
        fail(params.get('error_description') || `Đăng nhập bị từ chối (${oauthError}).`)
        return
      }

      if (!code) {
        fail('Thiếu authorization code trong phản hồi từ AuthServer.')
        return
      }

      const expectedState = getStoredState()
      if (!returnedState || !expectedState || returnedState !== expectedState) {
        fail('Phiên đăng nhập không hợp lệ (state không khớp). Vui lòng đăng nhập lại.')
        return
      }

      const codeVerifier = getStoredCodeVerifier()
      if (!codeVerifier) {
        fail('Thiếu dữ liệu PKCE cục bộ (có thể đã hết hạn hoặc mở ở tab khác). Vui lòng đăng nhập lại.')
        return
      }

      const expectedNonce = getStoredNonce()
      if (!expectedNonce) {
        fail('Thiếu dữ liệu phiên đăng nhập cục bộ (nonce). Vui lòng đăng nhập lại.')
        return
      }

      try {
        const res = await UserService.Auth.exchangeCodeForToken(code, codeVerifier)
        const { data } = res

        // Validate nonce TRƯỚC khi commit token vào Redux/localStorage — id_token không đúng nonce thì reject ngay, không giữ lại gì.
        const tokenNonce = data.id_token ? decodeIdTokenNonce(data.id_token) : null
        if (!tokenNonce || tokenNonce !== expectedNonce) {
          fail('Phiên đăng nhập không hợp lệ (nonce không khớp). Vui lòng đăng nhập lại.')
          return
        }

        dispatch(UserActions.setToken(data.access_token))
        dispatch(UserActions.setRefreshToken(data.refresh_token || ''))
        Utils.setLocalStorage('token', data.access_token)
        if (data.refresh_token) {
          Utils.setLocalStorage('refresh_token', data.refresh_token)
        }

        const returnTo = getStoredReturnTo()
        clearPkceTransaction()
        history.replace(returnTo || '/')
      } catch (err) {
        fail('Không thể xác thực authorization code với AuthServer. Vui lòng đăng nhập lại.')
      }
    }

    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (status === 'error') {
    return (
      <Result
        status="error"
        title="Đăng nhập không thành công"
        subTitle={errorMessage}
        extra={
          <Button type="primary" onClick={() => history.replace('/login')}>
            Quay lại đăng nhập
          </Button>
        }
      />
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
      }}
    >
      <Spin size="large" tip="Đang đăng nhập..." />
    </div>
  )
}
